import { Button, ErrorMessage, PageContainer, Spinner } from "@/components";
import { useCompanyInternshipsQuery, useCompanyProfileQuery } from "@/hooks/api/internship.queries";
import {
  ArrowLeft,
  ExternalLink,
  Globe,
  MapPin,
  Briefcase,
  ChevronRight,
  Users,
  IndianRupee,
  Clock,
  Wifi,
  Share2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

export default function DiscoverCompanyProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: company, isLoading: loadingCompany, isError: errorCompany, refetch } = useCompanyProfileQuery(id || "");
  const { data: internshipsData, isLoading: loadingJobs } = useCompanyInternshipsQuery(id || "", { limit: 50 });

  if (loadingCompany) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Spinner />
        <p className="text-gray-400 text-sm animate-pulse">Loading company profile...</p>
      </div>
    );
  }
  if (errorCompany || !company) {
    return <ErrorMessage message="Failed to load company profile" onRetry={() => refetch()} />;
  }

  const jobs = internshipsData?.internships || [];
  const name = company.name || "Unknown Company";

  // Extended info comes from internship items (not in the profile endpoint)
  const firstJob = jobs[0];
  const industry = firstJob?.company_industry || null;
  const description = firstJob?.company_description || null;
  const headquarters = firstJob?.company_headquarters || company.address || null;
  // Correct field name from API: website (not website_url)
  const websiteUrl = company.website || null;

  return (
    <PageContainer>
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="group flex items-center gap-2 text-sm text-gray-500 hover:text-violet-600 transition mb-6"
      >
        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        Back to Recruiter Hub
      </button>

      {/* Hero Banner */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <div className="h-44 bg-gradient-to-br from-violet-600 via-purple-700 to-indigo-700 relative">
          {/* Pattern overlay */}
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
          <div className="absolute top-4 right-4">
            <button className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium rounded-xl hover:bg-white/30 transition">
              <Share2 size={14} /> Share
            </button>
          </div>
          {/* Logo */}
          <div className="absolute -bottom-14 left-8 border-4 border-white bg-white rounded-2xl shadow-xl w-28 h-28 flex items-center justify-center overflow-hidden">
            {company.logo_url ? (
              <img src={company.logo_url} alt={name} className="w-full h-full object-contain p-2" />
            ) : (
              <span className="text-4xl font-black text-violet-600">{name.charAt(0)}</span>
            )}
          </div>
        </div>

        <div className="pt-20 pb-8 px-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 mb-1">{name}</h1>
            {industry && (
              <span className="inline-block px-3 py-1 bg-violet-50 text-violet-700 text-xs font-bold uppercase tracking-widest rounded-full border border-violet-100 mb-3">
                {industry}
              </span>
            )}
            {company.status === "verified" && (
              <span className="inline-block ml-2 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-widest rounded-full border border-emerald-100 mb-3">
                ✓ Verified
              </span>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              {headquarters && (
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} className="text-gray-400" />{headquarters}
                </span>
              )}
              {company.email && (
                <a href={`mailto:${company.email}`} className="flex items-center gap-1.5 hover:text-violet-600 transition">
                  <ExternalLink size={14} className="text-gray-400" />{company.email}
                </a>
              )}
              {company.phone && (
                <span className="flex items-center gap-1.5">
                  <Users size={14} className="text-gray-400" />{company.phone}
                </span>
              )}
              {websiteUrl && (
                <a
                  href={websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 hover:text-violet-600 transition"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Globe size={14} className="text-gray-400" />
                  {websiteUrl.replace(/^https?:\/\//, "")}
                  <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>

          <div className="flex gap-3 shrink-0">
            <Button
              variant="outline"
              className="border-violet-200 text-violet-700 hover:bg-violet-50"
            >
              Follow Company
            </Button>
            <Button
              className="bg-violet-600 hover:bg-violet-700 text-white"
              onClick={() => navigate("/discover/internships")}
            >
              Browse All Jobs
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* About Section */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-5">About the Company</h2>
            {description ? (
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{description}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">No public description provided by this company yet.</p>
            )}
          </div>

          {/* Contact / Address */}
          {company.address && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h2 className="text-base font-bold text-gray-900 mb-3">Address</h2>
              <p className="text-sm text-gray-600">{company.address}</p>
            </div>
          )}

          {/* Open roles stat */}
          <div className="bg-emerald-50 rounded-2xl border border-emerald-100 p-6 flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <Briefcase size={22} className="text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-extrabold text-gray-900">{loadingJobs ? "..." : jobs.length}</p>
              <p className="text-xs text-gray-500">Open Internships</p>
            </div>
          </div>
        </div>

        {/* Active Openings Sidebar */}
        <div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm sticky top-6 overflow-hidden">
            <div className="bg-gradient-to-br from-violet-600 to-indigo-700 px-6 py-4">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Briefcase size={16} /> Active Openings
              </h3>
              <p className="text-violet-200 text-xs mt-0.5">{loadingJobs ? "..." : `${jobs.length} role${jobs.length !== 1 ? "s" : ""} available`}</p>
            </div>

            <div className="p-4">
              {loadingJobs ? (
                <div className="py-10 flex justify-center"><Spinner /></div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Briefcase size={32} className="mx-auto mb-3 text-gray-200" />
                  <p className="text-sm">No active internships at the moment.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {jobs.map((job: any) => {
                    const jId = job.id || job._id;
                    return (
                      <div
                        key={jId}
                        onClick={() => navigate(`/discover/internships/${jId}`)}
                        className="group p-4 rounded-xl bg-gray-50 hover:bg-violet-50 border border-transparent hover:border-violet-100 cursor-pointer transition-all"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h4 className="font-semibold text-gray-900 group-hover:text-violet-700 text-sm leading-tight mb-2 line-clamp-2">
                              {job.title || job.position || "Untitled Role"}
                            </h4>
                            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                              {job.is_remote ? (
                                <span className="flex items-center gap-1"><Wifi size={10} className="text-emerald-500" /> Remote</span>
                              ) : job.location ? (
                                <span className="flex items-center gap-1"><MapPin size={10} />{job.location}</span>
                              ) : null}
                              {job.duration_weeks && (
                                <span className="flex items-center gap-1"><Clock size={10} />{job.duration_weeks}w</span>
                              )}
                              {job.stipend_min && (
                                <span className="flex items-center gap-1 text-emerald-600 font-semibold">
                                  <IndianRupee size={10} />₹{job.stipend_min.toLocaleString()}–{job.stipend_max?.toLocaleString()}/mo
                                </span>
                              )}
                              {job.status === "open" && (
                                <span className="text-emerald-600 font-bold">• Open</span>
                              )}
                            </div>
                          </div>
                          <ChevronRight size={15} className="text-gray-300 group-hover:text-violet-500 shrink-0 mt-0.5 transition" />
                        </div>
                      </div>
                    );
                  })}

                  <button
                    onClick={() => navigate("/discover/internships")}
                    className="w-full mt-2 py-3 text-xs font-bold text-violet-600 hover:text-violet-700 border border-violet-100 hover:border-violet-200 rounded-xl hover:bg-violet-50 transition flex items-center justify-center gap-1"
                  >
                    See All Internships <ChevronRight size={13} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
